import React, { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, CheckCircle2, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BUSINESS_TYPES = [
  { value: "ecommerce", label: "E-commerce / Retail" },
  { value: "professional_services", label: "Professional Services" },
  { value: "saas", label: "SaaS / Software" },
  { value: "freelancer", label: "Freelancer / Solo" },
  { value: "agency", label: "Agency / Consulting" },
  { value: "other", label: "Other" },
];

const EMPLOYEE_RANGES = [
  { value: "1", label: "Just me" },
  { value: "2-10", label: "2 – 10" },
  { value: "11-50", label: "11 – 50" },
  { value: "51-200", label: "51 – 200" },
  { value: "201-500", label: "201 – 500" },
  { value: "500+", label: "500+" },
];

export default function WaitlistForm() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [businessType, setBusinessType] = useState("");
  const [employees, setEmployees] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const isValid =
    name.trim().length > 1 &&
    /\S+@\S+\.\S+/.test(email) &&
    businessType &&
    employees;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!isValid || submitting) return;

    setSubmitting(true);
    try {
      await axios.post(`${API}/waitlist`, {
        name: name.trim(),
        email: email.trim().toLowerCase(),
        business_type: businessType,
        employees,
      });
      setDone(true);
      toast.success("Request received — welcome!", {
        description: "We'll approve your access within 24 hours. You can start the Free plan right now.",
      });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409) {
        toast.info("You're already in early access", {
          description: "This email is already approved. Sign in or sign up to start scanning.",
        });
      } else {
        toast.error("Something went wrong", {
          description:
            typeof detail === "string"
              ? detail
              : "Please check your details and try again.",
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div
        className="flex flex-col items-start text-left py-6"
        data-testid="waitlist-success"
      >
        <div className="h-12 w-12 rounded-full bg-emerald-50 flex items-center justify-center">
          <CheckCircle2 className="h-7 w-7 text-emerald-600" />
        </div>
        <h3 className="mt-5 font-display text-2xl font-semibold tracking-tight text-slate-900">
          You're in. Start scanning now.
        </h3>
        <p className="mt-2 text-slate-600 leading-relaxed">
          We've added <span className="font-medium text-slate-900">{email}</span>{" "}
          to the early access cohort. Create your account below to start with
          5 free scans this month — no credit card required.
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <a href="/register">
            <Button
              className="rounded-lg bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="waitlist-cta-register"
            >
              Create my account
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </a>
          <Button
            variant="outline"
            onClick={() => {
              setName("");
              setEmail("");
              setBusinessType("");
              setEmployees("");
              setDone(false);
            }}
            className="rounded-lg border-slate-300"
            data-testid="waitlist-add-another"
          >
            Add a teammate
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      className="flex flex-col gap-5"
      data-testid="waitlist-form"
      noValidate
    >
      <div>
        <p className="text-xs uppercase tracking-[0.22em] font-semibold text-blue-600">
          Request Access
        </p>
        <h3 className="mt-2 font-display text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900">
          Tell us a little about your business.
        </h3>
        <p className="mt-2 text-sm text-slate-600">
          30 seconds. Most early-access requests are approved within 24 hours.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="wl-name" className="text-sm font-medium text-slate-700">
            Full name
          </Label>
          <Input
            id="wl-name"
            type="text"
            placeholder="Jane Cooper"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="h-11 rounded-lg border-slate-300 bg-white focus-visible:ring-blue-600"
            data-testid="waitlist-name-input"
            required
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="wl-email" className="text-sm font-medium text-slate-700">
            Work email
          </Label>
          <Input
            id="wl-email"
            type="email"
            placeholder="jane@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="h-11 rounded-lg border-slate-300 bg-white focus-visible:ring-blue-600"
            data-testid="waitlist-email-input"
            required
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <Label className="text-sm font-medium text-slate-700">
            Business type
          </Label>
          <Select value={businessType} onValueChange={setBusinessType}>
            <SelectTrigger
              className="h-11 rounded-lg border-slate-300 bg-white text-left focus:ring-blue-600"
              data-testid="waitlist-business-type-select"
            >
              <SelectValue placeholder="Select your industry" />
            </SelectTrigger>
            <SelectContent>
              {BUSINESS_TYPES.map((b) => (
                <SelectItem
                  key={b.value}
                  value={b.value}
                  data-testid={`business-type-option-${b.value}`}
                >
                  {b.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label className="text-sm font-medium text-slate-700">
            Team size
          </Label>
          <Select value={employees} onValueChange={setEmployees}>
            <SelectTrigger
              className="h-11 rounded-lg border-slate-300 bg-white text-left focus:ring-blue-600"
              data-testid="waitlist-employees-select"
            >
              <SelectValue placeholder="How many employees?" />
            </SelectTrigger>
            <SelectContent>
              {EMPLOYEE_RANGES.map((r) => (
                <SelectItem
                  key={r.value}
                  value={r.value}
                  data-testid={`employees-option-${r.value}`}
                >
                  {r.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Button
        type="submit"
        disabled={!isValid || submitting}
        className="h-12 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold w-full sm:w-auto sm:self-start sm:px-7 disabled:opacity-60 disabled:cursor-not-allowed"
        data-testid="waitlist-submit-button"
      >
        {submitting ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Submitting...
          </>
        ) : (
          <>
            Request Early Access
            <ArrowRight className="ml-2 h-4 w-4" />
          </>
        )}
      </Button>

      <p className="text-xs text-slate-500">
        By submitting, you agree to receive occasional product updates. Unsubscribe any time.
      </p>
    </form>
  );
}
